import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { categoryColor, prettyType } from "@/lib/intel";
import { Boxes, Network as NetworkIcon } from "lucide-react";

function EntityRow({ e }) {
  return (
    <Link to={`/intel/entities/${e.id}`} data-testid={`entity-row-${e.id}`}
      className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 border-b border-slate-100">
      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: categoryColor(e.category) }} />
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate">{e.label}</div>
        <div className="text-[11px] text-slate-500 font-mono">{prettyType(e.type)}{e.network ? ` · ${e.network}` : ""}</div>
      </div>
      <span className="text-xs text-slate-400 font-mono shrink-0">{e.degree} link{e.degree === 1 ? "" : "s"}</span>
    </Link>
  );
}

export default function Entities() {
  const [entities, setEntities] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (type) params.type = type;
      const [{ data: ents }, { data: st }] = await Promise.all([
        api.get("/intel/entities", { params }),
        api.get("/intel/stats"),
      ]);
      setEntities(ents);
      setStats(st);
    } finally {
      setLoading(false);
    }
  }, [q, type]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const types = stats ? Object.keys(stats.by_type).sort() : [];

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl" data-testid="entities-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-blue-700 flex items-center gap-1.5"><Boxes className="h-4 w-4" /> Intelligence graph</div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-1">Entities</h1>
          <p className="text-sm text-slate-500 mt-1">Typed nodes across the network — people, identifiers, places, assets, ads and cases.</p>
        </div>
        <Link to="/intel/graph" data-testid="open-network-explorer"
          className="inline-flex items-center gap-1.5 text-sm bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded-md">
          <NetworkIcon className="h-4 w-4" /> Network explorer
        </Link>
      </header>

      {stats && (
        <div className="flex flex-wrap gap-3 text-sm" data-testid="intel-stats">
          <div className="border border-slate-200 bg-white rounded-md px-4 py-2"><span className="font-display font-bold text-lg">{stats.entities}</span> <span className="text-slate-500 text-xs">entities</span></div>
          <div className="border border-slate-200 bg-white rounded-md px-4 py-2"><span className="font-display font-bold text-lg">{stats.relationships}</span> <span className="text-slate-500 text-xs">relationships</span></div>
          <div className="border border-slate-200 bg-white rounded-md px-4 py-2"><span className="font-display font-bold text-lg">{stats.networks.length}</span> <span className="text-slate-500 text-xs">known networks</span></div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Input data-testid="entity-search" placeholder="Search entities…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        <select data-testid="entity-type-filter" value={type} onChange={(e) => setType(e.target.value)} className="border border-slate-300 rounded-md text-sm px-2 py-2">
          <option value="">All types</option>
          {types.map((t) => <option key={t} value={t}>{prettyType(t)} ({stats.by_type[t]})</option>)}
        </select>
      </div>

      <section className="border border-slate-200 bg-white rounded-md overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-slate-500" data-testid="entities-loading">Loading entities…</div>
        ) : entities.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="entities-empty">No entities match.</div>
        ) : (
          entities.map((e) => <EntityRow key={e.id} e={e} />)
        )}
      </section>
    </div>
  );
}
